// =============================================================================
// ui/tabs/AnalyticsSetup.tsx — the Setup Quality sub-tab.
// -----------------------------------------------------------------------------
// ⚠ PARTIAL, AND THE MISSING PART IS NAMED ON SCREEN. Beta's Setup Quality is
// FOUR cards:
//
//   Pattern Leaderboard          ← ported
//   Pattern × Exit-Model Pairing ← ported
//   Pattern × Exit-Reason        ← ported
//   Confluence Edge              ← not built here yet
//
// 🚨 THE EXIT-REASON CARD WAS LISTED HERE AS "genuinely blocked: no read carries
// the exit reason" AND IT WAS NOT BLOCKED. The reason is stamped on the stored
// post-exit walk at compute time, and that walk has been on the trade read since
// the Excursion tab shipped. The line survived a session that added the field.
// Seventh occurrence of the same failure: a sentence about what this client can
// do goes stale silently, because nothing on earth renders a comment false.
//
// The card on screen carries an invariant test now (D68); this header does not
// and cannot. Treat it as a note, and check `io/sync/` before believing it.
//
// The handoff called Setup Quality "the cheapest win"; that was true only of the
// leaderboard.
//
// ⚠ Same period rules as the other analytics tabs: this has its OWN period and
// ignores the global date filter. The read is unbounded.
//
// ⚠ USD ONLY for now, like every other ported analytics surface. The unit split
// in `patternLeaderboard` is still honoured — win/loss on the sign, totals on the
// unit value — so switching the display unit later changes one function argument
// rather than the maths.
// =============================================================================

import { useMemo, useState, useCallback } from 'react';
import { useAccounts } from '../state/accounts';
import { useExitModels } from '../state/exitModels';
import { useFilters } from '../state/filters';
import { useContracts } from '../state/contracts';
import { usePnlUnit } from '../state/usePnlUnit';
import { tradeValue, type PnlUnit } from '../../selectors/pnlUnit';
import { usePatterns } from '../state/patterns';
import { usePreferences } from '../state/preferences';
import { useRange } from '../state/range';
import { useTradeRows } from '../state/useTradeRows';
import {
  ANALYTICS_PERIODS, DEFAULT_ANALYTICS_PERIOD, analyticsPeriodLabel,
  dateInAnalyticsPeriod, type AnalyticsPeriod,
} from '../../selectors/analytics';
import {
  patternLeaderboard, sortLeaderboard, UNTAGGED_ID,
  type LeaderboardRow, type LeaderboardSortKey,
} from '../../selectors/patternLeaderboard';
import {
  buildPairingMatrix, cellKey, exitReasonRead, orderRowsByExtremeCell,
  pairingBestWorst, pairingCellSample, pairingCellValue, pairingExitReason,
  pairingHeatScale, PAIRING_EXIT_REASONS, PAIRING_METRICS, PAIRING_MIN_SAMPLE,
  PAIRING_MIN_TOTAL, PAIRING_NOISY_BELOW,
  type PairingAxis, type PairingCell, type PairingMatrix, type PairingMetric,
} from '../../selectors/patternPairing';
import {
  confluenceBadge, confluenceEdge, confluenceHeatBand, confluenceMarkReader,
  confluenceMetricValue, confluencePairGrid, patternConfluenceGrid,
  sortConfluenceRanking, stackingRead,
  CONFLUENCE_CELL_MIN, CONFLUENCE_METRICS, CONFLUENCE_MIN_SAMPLE,
  CONFLUENCE_MYDATA_THRESHOLD, CONFLUENCE_PREVIEW, CONFLUENCE_PREVIEW_PAIR,
  CONFLUENCE_PREVIEW_PATTERN, CONFLUENCE_ROBUST_SAMPLE, WIN_DEFINITIONS,
  type ConfluenceEdge, type ConfluenceGrid, type ConfluenceMetric,
  type ConfluenceRankRow, type StackingBucket, type WinDefinition,
} from '../../selectors/confluenceEdge';
import { tradesCountingForMetrics } from '../../selectors/accounts';
import { effectiveTradeDate } from '../../selectors/population';
import { hasPlannedStop } from '../../selectors/excursionStats';
import { num, pct, unitValue, unitCompact } from '../format';

const UNBOUNDED = { startDate: null, endDate: null };

/**
 * Which trades the figures are built from — beta's R / Points switch (TCR-77).
 *
 * ⚠ NOT A UNIT. `PnlUnit` is the unit, it is global, and it decides how the
 * numbers are written. This decides which trades there are to write about: `r`
 * keeps only trades a stop can be found for, because R is a ratio to risk and a
 * trade with no risk recorded has no R at all.
 */
type SetupBasis = 'points' | 'r';

const BASIS_LABEL: Readonly<Record<SetupBasis, string>> = {
  points: 'Points', r: 'R',
};

/**
 * What the figures cover, in words.
 *
 * 🔴 THE EXCLUSION CLAUSE IS THE WHOLE REASON THIS EXISTS. A switch that quietly
 * narrows the book is worse than no switch: every figure stays self-consistent
 * and all of them are built on fewer trades than the reader assumes. So when R
 * drops trades, the sentence says how many and why.
 *
 * ⚠ `num(n, 0)` — the default is two decimals, and "3.00 trades" is how the
 * first version of this read.
 */
function sampleNote(shown: number, total: number, basis: SetupBasis): string {
  const noun = (n: number) => `${num(n, 0)} ${n === 1 ? 'trade' : 'trades'}`;
  if (basis !== 'r' || shown === total) return noun(shown);
  const dropped = total - shown;
  return `${num(shown, 0)} of ${noun(total)} — ${num(dropped, 0)} `
    + `${dropped === 1 ? 'has' : 'have'} no stop recorded, `
    + 'so there is no R to judge them by';
}

/**
 * ⚠ THE OLD CLIENT'S COLUMN ORDER, and it is not arbitrary: the two shape
 * columns (win rate, avg R) sit before the two money columns (expectancy, PF)
 * and the totals come last. Reordering them makes the table read as a different
 * argument — "how much did this make" ahead of "how does it behave".
 *
 * ⚠ BEST and WORST were missing here and are two of beta's nine columns. On a
 * leaderboard sorted by expectancy they are the only thing that shows whether a
 * row's average came from a run of ordinary trades or from one outlier.
 */
const COLUMNS: readonly { key: LeaderboardSortKey; label: string; numeric: boolean }[] = [
  { key: 'name', label: 'Pattern', numeric: false },
  { key: 'sample', label: 'Trades', numeric: true },
  { key: 'winRate', label: 'Win Rate', numeric: true },
  { key: 'avgR', label: 'Avg R', numeric: true },
  { key: 'expectancy', label: 'Expectancy', numeric: true },
  { key: 'profitFactor', label: 'PF', numeric: true },
  { key: 'totalPnl', label: 'Total P&L', numeric: true },
  { key: 'bestPnl', label: 'Best', numeric: true },
  { key: 'worstPnl', label: 'Worst', numeric: true },
];

export function AnalyticsSetupTab() {
  const { accounts } = useAccounts();
  // ⚠ `facets` is deliberately NOT read — see the population memo below.
  const { accounts: selection } = useFilters();
  const { contracts } = useContracts();
  const { unit } = usePnlUnit();
  /**
   * One trade in the display unit, for the leaderboard and the two pairing grids.
   *
   * ⚠ NULL, NOT ZERO, where the trade has no value in the unit. `patternPairing`
   * counts those separately as `valued` precisely so a cell can say it averages
   * over fewer trades than it lists — a zero would be folded into the average and
   * drag every cell toward the middle.
   */
  const valueOf = useCallback(
    (t: { netPnl?: number | null }) => (
      t.netPnl == null ? null : tradeValue(t as never, unit, contracts ?? [])
    ),
    [unit, contracts],
  );
  const { prefs } = usePreferences();
  const { today } = useRange();
  const { patterns } = usePatterns();
  const { exitModels } = useExitModels();
  const mode = prefs?.dayBoundaryMode ?? null;

  const [period, setPeriod] = useState<AnalyticsPeriod>(DEFAULT_ANALYTICS_PERIOD);
  const [pemMetric, setPemMetric] = useState<PairingMetric>('expectancy');
  const [confMetric, setConfMetric] = useState<ConfluenceMetric>('winrate');
  const [winDef, setWinDef] = useState<WinDefinition>('1r');
  /**
   * ⚠ NULL MEANS "NOBODY HAS CHOSEN", and the default is then derived from how
   * much the trader has actually tagged. A hard default of Preview strands a
   * user with a full book on a canned demo; a hard default of My Data shows an
   * empty card to someone who has tagged nothing. Once they pick, their pick
   * holds — including picking the one the default would have chosen anyway.
   */
  const [confMode, setConfMode] = useState<'preview' | 'mydata' | null>(null);
  /**
   * 🚨 BETA'S R / POINTS SWITCH, AND IT IS NOT A UNIT TOGGLE (TCR-77).
   *
   * The global unit picker in the shell decides how every figure here is
   * WRITTEN, and this tab already follows it (`usePnlUnit` above). This decides
   * WHICH TRADES the figures are built from, which is a different question: a
   * trade with no stop has no R, so the R view cannot judge it at all and beta
   * drops it. Points keeps it.
   *
   * ⚠ Two controls that look alike and do different things is this change's own
   * trap, which is why the switch is labelled with what it changes rather than
   * with a unit, and why it is NOT folded into the shared unit state — doing
   * that would silently change the population of every other analytics tab from
   * one picker.
   *
   * 🔴 POINTS IS THE DEFAULT BECAUSE POINTS IS WHAT THIS TAB COUNTED BEFORE
   * TODAY. Defaulting to R would have narrowed every reader's figures the moment
   * this deployed, which is the same shape as TCR-94 and TCR-96 — a number that
   * moves without anyone asking it to.
   */
  const [basis, setBasis] = useState<SetupBasis>('points');
  /**
   * ⚠ EXPECTANCY, which is the old client's default and the right one.
   *
   * Sorted by TOTAL P&L a leaderboard ranks by how much you traded a setup, not
   * by how good it is: on Josh's book that puts VA Reversion (18 trades) above
   * Orderflow Reversal, which earns four times as much per trade. Expectancy is
   * per-trade, which is the question "which of my setups is best" actually asks.
   */
  const [sort, setSort] = useState<{ key: LeaderboardSortKey; dir: 'asc' | 'desc' }>(
    { key: 'expectancy', dir: 'desc' },
  );

  const { phase, rows, error } = useTradeRows(UNBOUNDED, mode);

  // ⚠ REWRITTEN. The first version read
  //     tradesForSelection(applyFacets(rows, facets, mode), selection, accounts)
  // which was wrong twice, and neither error was visible in the numbers:
  //
  //   • it APPLIED THE GLOBAL FACETS, which beta does not. Analytics reaches its
  //     population through `tradesForActiveAccounts()`, and the old client's own
  //     comment says facets apply "ONLY through tradesForActive()". Narrowing the
  //     Pattern facet to one setup made that setup the whole leaderboard.
  //   • it OMITTED `countsForMetrics`, so trades on accounts flagged
  //     exclude-from-metrics — SIM and market-replay — were ranked as edge
  //     alongside real ones. Beta's line is
  //     `tradesForActiveAccounts().filter(t => countsForMetrics(t))`.
  const inPeriod = useMemo(() => {
    if (!rows || !mode || !today) return null;
    const scoped = tradesCountingForMetrics(rows, selection, accounts ?? []);
    return scoped.filter((t) => dateInAnalyticsPeriod(effectiveTradeDate(t, mode), period, today));
  }, [rows, mode, today, selection, accounts, period]);

  /**
   * The population the figures are actually built from — `inPeriod` narrowed by
   * the R / Points switch.
   *
   * ⚠ IT FEEDS EVERYTHING BELOW, not just one card. That is the point of the
   * switch: the leaderboard, both pairing grids and the confluence card all have
   * to be built from the same set, or the page contradicts itself a few inches
   * apart — which is the divergence this tab has already been bitten by twice.
   *
   * ⚠ `hasPlannedStop` IS THE SHARED TEST, borrowed from the excursion code so
   * there is one answer to "could this trade be judged in R". MEASURED on the
   * live book 2026-11-01: of 6,198 trades, 2,533 carry `riskPoints` and **0**
   * carry a `plannedStop` without one. So on the TABLE projection — which
   * selects `riskPoints` and not `plannedStop` — this test is complete today by
   * accident rather than by construction. ▶ If `plannedStop` ever joins
   * `TABLE_KEYS`, this starts reading it and gets strictly better; nothing here
   * needs to change.
   */
  const counted = useMemo(() => {
    if (!inPeriod) return null;
    return basis === 'r' ? inPeriod.filter((t) => hasPlannedStop(t as never)) : inPeriod;
  }, [inPeriod, basis]);

  // ⚠ Resolves a name or returns null — `patternLeaderboard` turns null into
  // "(deleted pattern)" rather than dropping the trades.
  const namePattern = useMemo(() => {
    const byId = new Map((patterns ?? []).map((p) => [String(p.id), p.name]));
    return (id: string) => byId.get(String(id)) ?? null;
  }, [patterns]);

  const board = useMemo(() => {
    if (!counted) return null;
    // USD: the unit value IS net P&L, and it is never null here.
    return patternLeaderboard(counted, namePattern, (t) => valueOf(t));
  }, [counted, namePattern]);

  const sorted = useMemo(
    () => (board ? sortLeaderboard(board, sort.key, sort.dir) : null),
    [board, sort],
  );

  // ⚠ A DELETED EXIT MODEL STILL GETS A COLUMN, under a name that says what
  // happened. Dropping its trades would move the row totals against the
  // leaderboard three inches above, which is the divergence this whole tab keeps
  // being bitten by.
  const nameExitModel = useMemo(() => {
    const byId = new Map((exitModels ?? []).map((m) => [String(m.id), m.name]));
    return (id: string) => byId.get(String(id)) ?? '(deleted exit model)';
  }, [exitModels]);

  const namePatternRow = useMemo(
    () => (id: string) => namePattern(id) ?? '(deleted pattern)',
    [namePattern],
  );

  /**
   * ⚠ THE SAME POPULATION AS THE LEADERBOARD ABOVE — see the module header. The
   * old client narrows this card to reviewed trades only and does not narrow the
   * table above it, so an imported, auto-tagged, not-yet-reviewed trade is in one
   * and missing from the other. PD-23.
   */
  const pemMatrix = useMemo(() => {
    if (!counted) return null;
    const m = buildPairingMatrix(
      counted,
      (t) => (t.exitModelId ? String(t.exitModelId) : null),
      namePatternRow, nameExitModel,
      (t) => valueOf(t),
    );
    // ⚠ CATALOGUE ORDER, not the order trades happened to arrive in. Both axes
    // are lists the trader wrote and arranged; a grid whose columns reshuffle
    // when a period chip changes reads as a different grid.
    orderAxisBy(m.rows, (patterns ?? []).map((p) => String(p.id)));
    orderAxisBy(m.cols, (exitModels ?? []).map((e) => String(e.id)));
    return m;
  }, [counted, namePatternRow, nameExitModel, patterns, exitModels]);

  /**
   * The same shape asked of "why did I get out" instead of "how do I get out".
   *
   * ⚠ Only reasons the book actually contains become columns. A `Time` column
   * that is empty on every row invites the reading that time-stops are a habit
   * with no result, when in fact the trader has never taken one.
   */
  const reasonMatrix = useMemo(() => {
    if (!counted) return null;
    const label = new Map(PAIRING_EXIT_REASONS.map((r) => [r.key, r.label]));
    const m = buildPairingMatrix(
      counted,
      pairingExitReason,
      namePatternRow,
      (id) => label.get(id) ?? id,
      (t) => valueOf(t),
    );
    orderAxisBy(m.cols, [...PAIRING_EXIT_REASONS.map((r) => r.key)]);
    // ⚠ ROWS BY THEIR WORST CELL, most-left-behind first — this card's whole
    // point is which setup is being cut short, so the answer belongs at the top
    // rather than wherever the catalogue happens to put it.
    m.rows = orderRowsByExtremeCell(m, 'extension', 'worst');
    return m;
  }, [counted, namePatternRow]);

  /**
   * ⚠ ONE READER FOR THE WHOLE CARD. It carries the three rules that are easy to
   * lose — category parents excluded, orphans kept and flagged, both stored
   * shapes accepted — and every figure below goes through it, so the ranking and
   * the stacking buckets cannot come to disagree about what a trade was tagged
   * with.
   */
  const marksOfTrade = useMemo(
    () => confluenceMarkReader(prefs?.confluences ?? null),
    [prefs],
  );
  const marks = useMemo(
    () => (t: { confluences?: unknown }) => marksOfTrade(t.confluences ?? null),
    [marksOfTrade],
  );

  const edge = useMemo(
    () => (counted ? confluenceEdge(counted, marks, winDef) : null),
    [counted, marks, winDef],
  );
  const confPair = useMemo(
    () => (counted ? confluencePairGrid(counted, marks) : null),
    [counted, marks],
  );
  const confPattern = useMemo(
    () => (counted ? patternConfluenceGrid(counted, marks, namePatternRow) : null),
    [counted, marks, namePatternRow],
  );

  if (phase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your setup analytics</h3>
        <p className="muted">Nothing has been changed — this is a read problem. {error}</p>
      </div>
    );
  }

  if (!sorted) return <p className="muted">Loading setup analytics…</p>;

  const onSort = (key: LeaderboardSortKey) =>
    setSort((cur) => (cur.key === key
      ? { key, dir: cur.dir === 'desc' ? 'asc' : 'desc' }
      // A new column starts descending for numbers and ascending for the name,
      // which is what "best first" means for each.
      : { key, dir: key === 'name' ? 'asc' : 'desc' }));

  return (
    <div className="analytics-tab">
      <div className="an-periodbar">
        {ANALYTICS_PERIODS.map((p) => (
          <button
            key={p.id} type="button"
            className={`period-chip${period.id === p.id ? ' active' : ''}`}
            aria-pressed={period.id === p.id}
            onClick={() => setPeriod({ id: p.id })}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* ⚠ ABOVE THE EMPTY-STATE BRANCH, deliberately. In R mode on a book where
          nothing carries a stop, `sorted` is empty — and a switch rendered
          inside the populated branch would vanish at exactly the moment the
          reader needs it to switch back. */}
      <div className="an-periodbar">
        <span className="muted">Count trades by</span>
        {(['points', 'r'] as const).map((b) => (
          <button
            key={b} type="button"
            className={`period-chip${basis === b ? ' active' : ''}`}
            aria-pressed={basis === b}
            onClick={() => setBasis(b)}
          >
            {BASIS_LABEL[b]}
          </button>
        ))}
        {/* 🔴 THE COUNT IS THE POINT. A control that quietly halves the sample is
            worse than no control: every figure stays self-consistent and all of
            them are built on a narrower book than the reader assumes. So the
            size of the set is on screen, and it moves when the switch moves. */}
        <span className="muted" role="status">
          {counted && inPeriod ? sampleNote(counted.length, inPeriod.length, basis) : ''}
        </span>
      </div>

      {sorted.length === 0 ? (
        <div className="card">
          <h3>No trades in {analyticsPeriodLabel(period)}</h3>
          <p className="muted">Widen the period above to compare setups over more history.</p>
        </div>
      ) : (
        <>
          <div className="section-header">
            <h3>Pattern Leaderboard</h3>
            <span className="muted">{analyticsPeriodLabel(period)}</span>
          </div>

          <div className="table-wrap trade-table-card">
            <table className="table trades">
              <thead>
                <tr>
                  {COLUMNS.map((c) => {
                    const active = sort.key === c.key;
                    return (
                      <th
                        key={c.key}
                        className={[c.numeric ? 'num' : '', 'sortable', active ? 'sort-active' : '']
                          .filter(Boolean).join(' ')}
                        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                        onClick={() => onSort(c.key)}
                      >
                        {c.label}
                        <span className="sort-arrow">
                          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '▲▼'}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => <Row unit={unit} key={r.id} r={r} />)}
              </tbody>
            </table>
          </div>

          <p className="muted">
            Win rate and profit factor classify on the dollar sign, so they hold whatever
            display unit is shown. Scratches count in <b>Trades</b> but not in the win-rate
            denominator — a day of scratches is not a 0% win rate.
          </p>

          <PairingCard unit={unit} matrix={pemMatrix} metric={pemMetric} onMetric={setPemMetric} />
          <ExitReasonCard unit={unit} matrix={reasonMatrix} />
          <ConfluenceCard
            edge={edge} pair={confPair} pattern={confPattern}
            def={winDef} metric={confMetric}
            mode={confMode ?? ((edge?.totalTagged ?? 0) >= CONFLUENCE_MYDATA_THRESHOLD
              ? 'mydata' : 'preview')}
            onDef={setWinDef} onMetric={setConfMetric} onMode={setConfMode}
          />
        </>
      )}

      {/* ⚠ NAMED, not silently absent — saying which card is missing, and why,
          is the difference between "not built yet" and "broken".

          🚨 THIS COPY HAS NOW GONE STALE TWICE, IN THE SAME DIRECTION BOTH TIMES.
          It first claimed all three cards needed data this client does not read;
          two of the three had shipped with the trade form (D71). It was rewritten
          to say the exit-reason heatmap "needs the exit reason, which no read
          carries" — and the reason was already arriving on every trade, stamped
          on the stored walk that the Excursion tab reads. Corrected again here.

          Seventh occurrence, and the lesson has not changed: a sentence about
          what is missing gets LESS true as the work goes well, and nothing on the
          screen renders it false. Re-read it whenever anything on this tab
          ships. */}
      <div className="card">
        <h3>Not on this screen yet</h3>
        {/* 🚨 IT USED TO STOP AT "BEHAVIOUR RATHER THAN A CARD", AND THAT UNDERSOLD
            THE GAP. Every card is indeed built — the suite asserts all four are
            headings — but two CONTROLS beta carries are not, and one of them moves
            numbers rather than decorating them. A card headed "what is missing"
            that lists only the cosmetic half is the same failure as claiming
            nothing is missing, one step quieter. */}
        <p className="muted">
          Every card beta shows on Setup Quality is here. What is missing is
          <b> control and behaviour</b> rather than a card:
        </p>
        <ul className="muted">
          <li>clicking a row or a cell to open the trades behind it</li>
          <li>the <b>?</b> explainers</li>
          {/* ⚠ THE R / POINTS ENTRY WAS HERE AND IS NOW BUILT (TCR-77). It is
              gone rather than reworded: this tab's own test guard fails an
              absence sentence that names a control the tab renders, and the
              switch is a control the tab renders. Beta's explanation of WHY the
              two differ did not evaporate with it — it moved to the switch and
              to `SetupBasis`, which is where a reader looking at the control
              will actually find it. */}
          <li>a custom date range — this tab offers the presets only</li>
        </ul>
        <p className="muted">
          Neither of those changes a number — the first opens trades you can
          already see, the second only moves the window.
        </p>
      </div>
    </div>
  );
}

// ── the pairing matrices ────────────────────────────────────────────────────

/**
 * Reorder an axis IN PLACE to a canonical id order, unknown ids last.
 *
 * ⚠ `indexOf` returning −1 for an id the catalogue no longer holds is exactly
 * wrong — it would sort a deleted setup to the FRONT of the grid. Unknown goes
 * to the end, keeping its relative order.
 */
function orderAxisBy(axis: PairingAxis[], order: readonly string[]): void {
  const at = new Map(order.map((id, i) => [id, i]));
  axis.sort((a, b) => (at.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (at.get(b.id) ?? Number.MAX_SAFE_INTEGER));
}

/**
 * Header height for the rotated column labels.
 *
 * ⚠ A -45° label needs vertical room in proportion to its LENGTH, so this is
 * sized from the longest one. The old client's constants, kept: below ~108px the
 * shortest labels still fit, above it every extra character costs about 5.9px.
 * Fixed heights either waste half the card or clip the longest exit model's name
 * off the top — and clipping is invisible to every test, because jsdom applies
 * no CSS.
 */
function headHeight(labels: readonly string[]): number {
  const max = Math.max(0, ...labels.map((l) => l.length));
  return Math.max(108, Math.round(max * 5.9) + 20);
}

/**
 * Right padding for the same rotated labels — 🚨 AND THE OLD CLIENT'S FIXED 44px
 * IS NOT ENOUGH, MEASURED ON JOSH'S OWN CATALOGUE.
 *
 * A label rotated -45° about its own left-bottom corner reaches as far RIGHT as
 * it does UP. The old client sizes the height from the longest label and then
 * pins the horizontal room at a constant, so its widest column name overhangs
 * the table by whatever the two disagree about. On this book that is "Trailing
 * Stop with Tightening": measured on the running page it ran **58px past the
 * card**, arriving on screen as "TRAILING STOP WITH".
 *
 * ⚠ NO TEST COULD SEE IT — jsdom applies no CSS, so the whole string is in the
 * DOM either way. Found by measuring the rendered label against its container,
 * the same way the Coach's Report's truncated verdict column was (D129).
 *
 * The extra room is dead space in a scroll box, which costs nothing; a clipped
 * column heading costs the reader the column.
 */
function headPadRight(labels: readonly string[]): number {
  const max = Math.max(0, ...labels.map((l) => l.length));
  return Math.max(44, Math.round(max * 5));
}

const BAND_CLASS: Record<string, string> = {
  'pos-3': 'h-pos-3', 'pos-2': 'h-pos-2', 'pos-1': 'h-pos-1',
  neu: 'h-neu', 'neg-1': 'h-neg-1', 'neg-2': 'h-neg-2',
};

/**
 * One cell's figure, in the metric's own units.
 *
 * 🚨 THE SIGN COMES OFF THE ROUNDED VALUE, NOT THE RAW ONE. A median of −0.02R
 * signs itself negative and then rounds to "0.0", printing **−0.0R** — a minus
 * in front of a zero, on the card that tells the trader whether an exit was
 * early or late. This session has already shipped that once as `+0.00R`
 * (PD-16's family), and it was found on the running page rather than by any
 * test, because a fixture has to be within a rounding step of zero to show it.
 */
function formatCell(v: number | null, metric: PairingMetric, unit: PnlUnit): string {
  if (v == null) return '—';
  if (metric === 'winrate') return pct(v, 0);
  if (metric === 'count') return String(v);
  if (metric === 'extension') {
    // ⚠ `|| 0` IS LOAD-BEARING: `Math.round(-0.02 * 10) / 10` is **−0**, which is
    // `>= 0` to a comparison and still formats as "-0.0". Only coercing it to a
    // plain zero closes both ends.
    const r = (Math.round(v * 10) / 10) || 0;
    // ⚠ AND NO SIGN AT ALL ON A ROUNDED ZERO. At one decimal, −0.02R and +0.02R
    // are the same fact — "essentially nothing kept running" — and putting a
    // plus in front of a negative median is as wrong as the minus was.
    return `${r > 0 ? '+' : ''}${num(r, 1)}R`;
  }
  return unitCompact(v, unit);
}

function PairingCard({ unit,
  matrix, metric, onMetric,
}: {
  unit: PnlUnit;
  matrix: PairingMatrix | null;
  metric: PairingMetric;
  onMetric: (m: PairingMetric) => void;
}) {
  if (!matrix) return null;

  const chips = (
    <div className="pair-chips">
      {PAIRING_METRICS.map((m) => (
        <button
          key={m.key} type="button"
          className={`period-chip${metric === m.key ? ' active' : ''}`}
          aria-pressed={metric === m.key}
          onClick={() => onMetric(m.key)}
        >
          {m.label}
        </button>
      ))}
    </div>
  );

  const header = (
    <div className="section-header">
      <h3>Pattern × Exit-Model Pairing</h3>
      {chips}
    </div>
  );

  // ⚠ THE COVERAGE LINE IS NOT OPTIONAL FURNITURE. This grid describes only the
  // trades carrying BOTH tags, and on a book where most trades carry neither it
  // is a view of a small corner. Saying so where the reader is already looking
  // beats a caveat under twenty rows of colour.
  const coverage = (
    <p className="muted">
      {matrix.total} of {matrix.total + matrix.untagged} trades in this period carry both
      a setup and an exit model. The other {matrix.untagged} are not on this grid.
    </p>
  );

  if (matrix.total < PAIRING_MIN_TOTAL) {
    return (
      <div className="card">
        {header}
        <p className="muted">
          Only <b>{matrix.total}</b> {matrix.total === 1 ? 'trade' : 'trades'} in this period
          carry both a setup <b>and</b> an exit model, and a grid of one-trade cells reads
          100% or 0% everywhere. Tag more trades, or widen the period — the signal starts
          to mean something around 25–50.
        </p>
      </div>
    );
  }

  if (metric === 'extension' && matrix.withExtension === 0) {
    return (
      <div className="card">
        {header}
        <p className="muted">
          No trade here has a stored post-exit run, so there is nothing to measure. That
          walk is computed in the old client behind its Recompute button — one metered bar
          fetch per trade — and this client reads what it left behind rather than fetching
          any of its own.
        </p>
        {coverage}
      </div>
    );
  }

  const bw = pairingBestWorst(matrix, metric);
  const lowerBetter = metric === 'extension';

  return (
    <div className="card">
      {header}
      <MatrixTable unit={unit} matrix={matrix} metric={metric} />

      <div className="pem-verdict">
        {bw.best && bw.worst && bw.qualifying > 1 ? (
          <>
            <div className="pem-vline">
              <b className="pem-best">{lowerBetter ? 'Best fit:' : 'Strongest pairing:'}</b>{' '}
              <b>{bw.best.row.name}</b> × <b>{bw.best.col.name}</b> —{' '}
              <PairingSummary unit={unit} metric={metric} pick={bw.best} />
            </div>
            <div className="pem-vline">
              <b className="pem-worst">{lowerBetter ? 'Worst fit:' : 'Weakest pairing:'}</b>{' '}
              <b>{bw.worst.row.name}</b> × <b>{bw.worst.col.name}</b> —{' '}
              <PairingSummary unit={unit} metric={metric} pick={bw.worst} />
            </div>
          </>
        ) : bw.best ? (
          /* ⚠ ONE QUALIFYING CELL IS NOT A RANKING, and the sentence has to say so
             rather than crowning it. "Strongest pairing" off the only cell that
             cleared the floor is a comparison with nothing on the other side. */
          <div>
            Only one pairing has reached {PAIRING_MIN_SAMPLE} trades:{' '}
            <b>{bw.best.row.name}</b> × <b>{bw.best.col.name}</b> —{' '}
            <PairingSummary unit={unit} metric={metric} pick={bw.best} /> Nothing to compare it
            against yet.
          </div>
        ) : (
          <div>
            No pairing has reached {PAIRING_MIN_SAMPLE} trades yet, so nothing on this grid
            is coloured against anything else.
          </div>
        )}
      </div>

      <p className="muted">
        Each cell pairs a setup (row) with an exit model (column). Colour is relative to
        this grid — blue strong, warm weak. Cells below {PAIRING_MIN_SAMPLE} trades read
        “insufficient” rather than a figure, and an empty pairing reads “—”.{' '}
        <b>Extension</b> is the median R that kept running <i>after</i> you were out, so
        there the scale is inverted: blue means the exit captured the move.
      </p>
      {coverage}
    </div>
  );
}

/** The per-pairing figures behind a verdict line. Always the same three, always in the same order. */
function PairingSummary(
  { metric, pick, unit }:
  { metric: PairingMetric; pick: { cell: PairingCell; value: number }; unit: PnlUnit },
) {
  const c = pick.cell;
  const wr = pairingCellValue(c, 'winrate');
  if (metric === 'extension') {
    return (
      <>
        median <b>{formatCell(pick.value, 'extension', unit)}</b> left on the table over{' '}
        {c.extR.length} measurable {c.extR.length === 1 ? 'run' : 'runs'} of {c.count} trades.
      </>
    );
  }
  return (
    <>
      {c.count} trades, <b>{unitCompact(pairingCellValue(c, 'expectancy'), unit)}</b> expectancy,{' '}
      {/* ⚠ An em dash on a cell of nothing but scratches. A win rate of 0% there
          would say every one of them lost. */}
      {wr == null ? '—' : pct(wr, 0)} win rate, {unitCompact(pairingCellValue(c, 'totalpnl'), unit)} total.
    </>
  );
}

function MatrixTable(
  { matrix, metric, unit }:
  { matrix: PairingMatrix; metric: PairingMetric; unit: PnlUnit },
) {
  const scale = pairingHeatScale(matrix, metric);
  const labels = matrix.cols.map((c) => c.name);
  const headH = headHeight(labels);

  return (
    <div className="pairwise-wrap" style={{ ['--pw-pad-r' as string]: `${headPadRight(labels)}px` }}>
      <table className="pairwise" style={{ ['--pw-head-h' as string]: `${headH}px` }}>
        <thead>
          <tr>
            <th className="pw-corner" />
            {matrix.cols.map((c) => (
              <th key={c.id} className="pw-col" scope="col"><span>{c.name}</span></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((r) => (
            <tr key={r.id}>
              <th className="pw-row" scope="row">{r.name}</th>
              {matrix.cols.map((c) => {
                const cell = matrix.cells.get(cellKey(r.id, c.id));
                if (!cell || cell.count === 0) {
                  return <td key={c.id} className="pw-empty">—</td>;
                }
                const sample = pairingCellSample(cell, metric);
                const v = sample >= PAIRING_MIN_SAMPLE ? pairingCellValue(cell, metric) : null;
                // ⚠ THREE STATES, THREE RENDERINGS. "insufficient" means trades
                // exist and are too few to read; "—" means none at all. Merging
                // them is how a grid quietly implies a setup was never traded
                // with an exit model it was traded with twice.
                if (v == null) {
                  return <td key={c.id} className="pw-empty">insufficient</td>;
                }
                const title = [
                  `${r.name} × ${c.name}: ${cell.count} trade${cell.count === 1 ? '' : 's'}`,
                  `expectancy ${unitCompact(pairingCellValue(cell, 'expectancy'), unit)}`,
                  `win rate ${pairingCellValue(cell, 'winrate') == null ? '—' : pct(pairingCellValue(cell, 'winrate'), 0)}`,
                  `total ${unitCompact(pairingCellValue(cell, 'totalpnl'), unit)}`,
                ].join(', ');
                return (
                  <td key={c.id} className={BAND_CLASS[scale.band(v)]} title={title}>
                    {formatCell(v, metric, unit)}
                    {metric === 'count' ? null : (
                      // ⚠ THE SAMPLE SHOWN IS THE METRIC'S OWN. Under Extension
                      // that is the number of measurable walks, which is usually
                      // far smaller than the cell — printing the trade count
                      // there would overstate what the median rests on.
                      <span className="pw-n">
                        n={sample}{sample < PAIRING_NOISY_BELOW ? ' ⚠' : ''}
                        {metric === 'extension' && sample < cell.count ? ` of ${cell.count}` : ''}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Pattern × Exit-Reason — the diagnostic twin.
 *
 * ⚠ IT DOES NOT VANISH WHEN IT IS THIN. The old client returns an empty string
 * when there are fewer than two reasons or two setups, so on a book that has not
 * been recomputed the card is simply not there and nothing says why. A missing
 * card is indistinguishable from a card that failed.
 */
function ExitReasonCard({ matrix, unit }: { matrix: PairingMatrix | null; unit: PnlUnit }) {
  if (!matrix) return null;

  const header = (
    <div className="section-header">
      <h3>Pattern × Exit-Reason</h3>
      <span className="muted">median R left on the table</span>
    </div>
  );

  if (matrix.withExtension === 0 || matrix.cols.length === 0) {
    return (
      <div className="card">
        {header}
        <p className="muted">
          Nothing here carries a stored post-exit run, so there is no “what happened after
          I got out” to read. Those walks are computed in the old client behind its
          Recompute button; this client reads them and never fetches its own.
        </p>
      </div>
    );
  }

  const read = exitReasonRead(matrix);

  return (
    <div className="card">
      {header}
      <MatrixTable unit={unit} matrix={matrix} metric="extension" />

      {/* ⚠ NULL IS A REAL ANSWER HERE and the card prints nothing rather than
          reaching for the least-bad cell. A coaching sentence generated because
          the template existed is the exact shape of the old client's inverted
          "a clean day is worth $X more" (PD-22). */}
      {read ? (
        <div className="ai-callout">
          <div className="icon">✦</div>
          <div className="body">
            <div className="tag">Coach — Pattern × Exit-Reason</div>
            Your biggest leave-on-the-table is <b>{read.pattern}</b> exited by{' '}
            <b>{read.reasonLabel.toLowerCase()}</b> — a median{' '}
            <b>+{num(read.medianR, 1)}R</b> kept running after you were out, over{' '}
            {read.sample} measurable {read.sample === 1 ? 'run' : 'runs'}. In short:{' '}
            {read.action}.
          </div>
        </div>
      ) : (
        <p className="muted">
          No setup-and-reason pairing leaves a median of half a unit of risk or more behind,
          so there is no single read worth acting on. That is the good version of this card.
        </p>
      )}

      <p className="muted">
        The same grid asked from the other end: not <i>how</i> you exit but <i>why</i>, and
        how much the market kept going afterwards. Warm cells are the ones that kept running
        — likely too early; blue is well-timed. The window is the 30 minutes after your
        exit, and the run is only ever credited against a real stop, never a peak nobody
        was holding.
      </p>
    </div>
  );
}

function Row({ r, unit }: { r: LeaderboardRow; unit: PnlUnit }) {
  const s = r.stats;
  return (
    <tr className={r.id === UNTAGGED_ID ? 'unannotated' : ''}>
      <td>
        {r.name}
        {/* ⚠ The untagged bucket is LABELLED, because it is the one row that is not
            a finding about a setup — it is a finding about the annotation backlog. */}
        {r.id === UNTAGGED_ID ? (
          <span className="muted"> — tag these to compare them</span>
        ) : null}
      </td>
      <td className="num">{s.sample}</td>
      {/* ⚠ `pct` APPENDS a sign, it does not scale — every other call site passes
          a 0–100 value straight in. Dividing by 100 here rendered every win rate
          as "1%" (0.55 → toFixed(0) → "1"), which looked plausible enough on a
          screenshot to miss and was caught only by reading the live numbers
          against the W/L counts beside them. */}
      <td className="num">
        {pct(s.winRate, 0)}
        <span className="muted"> {s.wins}W/{s.losses}L</span>
      </td>
      {/* ⚠ AN EM DASH WHEN NO TRADE IN THE ROW RECORDED AN R, not `0.00`. The old
          client prints a flat average there, beside a real win rate and a real
          profit factor, on a pattern nobody ever gave a stop. Same family as
          PD-16 on the Analytics hero. */}
      <td className="num">
        {s.avgR == null ? '—' : num(s.avgR, 2)}
        {s.avgR != null && s.rSample < s.sample
          ? <span className="muted"> of {s.rSample}</span> : null}
      </td>
      <td className={`num ${s.expectancy > 0 ? 'green' : s.expectancy < 0 ? 'red' : ''}`}>
        {unitValue(s.expectancy, unit)}
      </td>
      {/* ⚠ "∞" is a REAL state — winners and no losses — and is not the same as a
          big number. Rendering it as one would make it sortable-looking and wrong. */}
      <td className="num">{s.profitFactor === Infinity ? '∞' : num(s.profitFactor, 2)}</td>
      <td className={`num ${s.totalPnl > 0 ? 'green' : s.totalPnl < 0 ? 'red' : ''}`}>
        {unitValue(s.totalPnl, unit)}
      </td>
      {/* ⚠ The two outliers. On a table sorted by expectancy they are the only
          columns that say whether a row's average came from a run of ordinary
          trades or from one trade carrying the whole pattern. */}
      <td className="num green">{unitValue(s.bestPnl, unit)}</td>
      <td className="num red">{unitValue(s.worstPnl, unit)}</td>
    </tr>
  );
}

// ── Confluence Edge ─────────────────────────────────────────────────────────

const BADGE_TEXT: Record<string, string> = {
  reliable: 'Reliable', sufficient: 'Sufficient', limited: 'Limited',
  underperformer: 'Underperformer',
};

/** A figure in R, signed — with no sign at all on a rounded zero (see `formatCell`). */
function rr(v: number | null): string {
  if (v == null) return '—';
  const r = (Math.round(v * 10) / 10) || 0;
  return `${r > 0 ? '+' : ''}${num(r, 1)}R`;
}

function ConfluenceCard({
  edge, pair, pattern, def, metric, mode,
  onDef, onMetric, onMode,
}: {
  edge: ConfluenceEdge | null;
  pair: ConfluenceGrid | null;
  pattern: ConfluenceGrid | null;
  def: WinDefinition;
  metric: ConfluenceMetric;
  mode: 'preview' | 'mydata';
  onDef: (d: WinDefinition) => void;
  onMetric: (m: ConfluenceMetric) => void;
  onMode: (m: 'preview' | 'mydata') => void;
}) {
  if (!edge) return null;

  const preview = mode === 'preview';
  const shown = preview ? CONFLUENCE_PREVIEW : edge;
  const shownPair = preview ? CONFLUENCE_PREVIEW_PAIR : pair;
  const shownPattern = preview ? CONFLUENCE_PREVIEW_PATTERN : pattern;
  const ranking = sortConfluenceRanking(shown.ranking, metric);

  const toggle = (
    <div className="pair-chips">
      <button
        type="button" className={`period-chip${preview ? ' active' : ''}`}
        aria-pressed={preview} onClick={() => onMode('preview')}
      >Preview Data</button>
      <button
        type="button" className={`period-chip${!preview ? ' active' : ''}`}
        aria-pressed={!preview} onClick={() => onMode('mydata')}
      >My Data</button>
    </div>
  );

  const header = (
    <div className="section-header">
      <h3>Confluence Edge</h3>
      {toggle}
    </div>
  );

  if (!preview && edge.totalTagged === 0) {
    return (
      <div className="card">
        {header}
        <p className="muted">
          None of the {edge.population} trades in this period has a confluence tagged, so
          there is nothing to rank. Tag them on a trade, or switch to <b>Preview Data</b> to
          see what this card does with a full book.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      {header}

      {/* 🚨 THE WARNING LEADS. It is the honest headline of this whole card on a
          book like this one, and burying it under twenty-seven rows of 100% is
          exactly how a small sample becomes a strategy. */}
      {preview ? (
        <div className="conf-banner demo">
          <span className="conf-banner-icon">🎓</span>
          <div>
            <b>Preview Mode — this is not your data.</b> A curated demo showing what this
            card looks like on a fully tagged book. You have tagged{' '}
            <b>{edge.totalTagged}</b> of your {edge.population} trades. Switch to{' '}
            <b>My Data</b> for your own.
          </div>
        </div>
      ) : edge.totalTagged < CONFLUENCE_ROBUST_SAMPLE ? (
        <div className="conf-banner warn">
          <span className="conf-banner-icon">⚠</span>
          <div>
            <b>Small sample.</b> These are your tagged trades — <b>{edge.totalTagged}</b> of{' '}
            {edge.population} — spread across <b>{edge.distinct}</b> confluences, and only{' '}
            <b>{edge.aboveFloor}</b> of those {edge.aboveFloor === 1 ? 'has' : 'have'} reached{' '}
            {CONFLUENCE_MIN_SAMPLE} trades. Everything below that line reads 100% or 0% off
            one or two trades. Treat all of it as directional until you have logged{' '}
            {CONFLUENCE_ROBUST_SAMPLE}+.
          </div>
        </div>
      ) : null}

      <div className="conf-controls">
        <span className="conf-control-label">Success metric</span>
        <div className="pair-chips">
          {CONFLUENCE_METRICS.map((m) => (
            <button
              key={m.key} type="button"
              className={`period-chip${metric === m.key ? ' active' : ''}`}
              aria-pressed={metric === m.key} onClick={() => onMetric(m.key)}
            >{m.label}</button>
          ))}
        </div>
      </div>
      <div className="conf-controls">
        <span className="conf-control-label">Win definition</span>
        <div className="pair-chips">
          {WIN_DEFINITIONS.map((w) => (
            <button
              key={w.key} type="button"
              className={`period-chip${def === w.key ? ' active' : ''}`}
              aria-pressed={def === w.key} onClick={() => onDef(w.key)}
            >{w.label}</button>
          ))}
        </div>
      </div>

      <h4 className="conf-subhead">Single-confluence ranking</h4>
      <div className="table-wrap">
        <table className="table trades">
          <thead>
            <tr>
              <th>Confluence</th>
              <th className="num">Trades</th>
              <th className="num">Win Rate</th>
              <th className="num">Expectancy</th>
              <th className="num">Median R</th>
              <th>Sample</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((row) => <ConfluenceRow key={row.id} row={row} metric={metric} />)}
          </tbody>
        </table>
      </div>

      <h4 className="conf-subhead">Stacking — does layering confluences improve outcomes?</h4>
      <StackingTiles buckets={shown.stacking} metric={metric} def={def} />

      <ConfluenceGrids pair={shownPair} pattern={shownPattern} preview={preview} />

      <p className="muted">
        <b>Correlation, not causation.</b> These numbers describe the patterns in your
        tagged history — they do <i>not</i> prove a confluence <i>caused</i> your wins. Size,
        time of day and market regime overlap with what you choose to tag. Use it to refine
        setup grading, not as a signal on its own.
      </p>
    </div>
  );
}

function ConfluenceRow({ row, metric }: { row: ConfluenceRankRow; metric: ConfluenceMetric }) {
  const s = row.stats;
  const badge = confluenceBadge(s);
  const small = badge.kind === 'small';
  // ⚠ Computed OUTSIDE the `className` expressions on purpose: the style guard
  // reads string literals inside `className={…}` as class names, so a helper
  // called as `strong('winrate')` there reported three classes that do not
  // exist. A guard that cries wolf gets switched off.
  const wrCls = metric === 'winrate' ? 'num strong' : 'num';
  const expCls = metric === 'expectancy' ? 'num strong' : 'num';
  const medCls = metric === 'medianR' ? 'num strong' : 'num';
  return (
    <tr className={small ? 'conf-dim' : ''}>
      <td>
        {row.name}
        {/* ⚠ AN ORPHAN IS LABELLED, not hidden and not passed off as a name. This
            confluence was deleted from Settings and the trades tagged with it
            still exist — the old client prints the raw id and leaves the reader
            to wonder why `volpro-lvn` is in a list of `E-UPPER`. */}
        {row.orphan ? <span className="muted"> — removed from your list</span> : null}
      </td>
      <td className="num">{s.n}</td>
      {/* ⚠ THE DENOMINATOR IS THE TRADES THIS DEFINITION COULD JUDGE, and where
          that is fewer than the row it says so. Beta counts an unmeasurable
          trade as a loss under "1R+" and as a WIN under "Hit target". */}
      <td className={wrCls}>
        {s.winRate == null ? '—' : pct(s.winRate, 0)}
        {s.winRate != null && s.scored < s.n
          ? <span className="muted"> of {s.scored}</span> : null}
      </td>
      <td className={expCls}>{rr(s.expectancy)}</td>
      <td className={medCls}>{rr(s.medianR)}</td>
      <td>
        <span className={`conf-badge ${badge.kind}`}>
          {badge.kind === 'small'
            ? `Small sample · ${badge.more} more`
            : BADGE_TEXT[badge.kind]}
        </span>
      </td>
    </tr>
  );
}

function StackingTiles({
  buckets, metric, def,
}: { buckets: readonly StackingBucket[]; metric: ConfluenceMetric; def: WinDefinition }) {
  const read = stackingRead(buckets);
  // ⚠ "Best" only among buckets big enough to mean anything, and never a bucket
  // the definition could barely judge.
  const adequate = buckets.filter((b) => b.stats.scored >= 15);
  const best = adequate.reduce<StackingBucket | null>((acc, b) => {
    const v = confluenceMetricValue(b.stats, metric);
    const a = acc ? confluenceMetricValue(acc.stats, metric) : null;
    return v != null && (a == null || v > a) ? b : acc;
  }, null);

  return (
    <>
      <div className="conf-tiles">
        {buckets.map((b) => {
          const s = b.stats;
          const isBest = best != null && best.count === b.count;
          const thin = s.scored < 15;
          return (
            <div key={b.count} className={`conf-tile${isBest ? ' best' : ''}`}>
              <div className="muted">{b.label}{isBest ? ' ★' : ''}</div>
              <div className="conf-tile-v">{s.winRate == null ? '—' : pct(s.winRate, 0)}</div>
              <div className="muted conf-tile-sub">
                n={s.n}{thin ? ' ⚠ thin' : ''} · exp {rr(s.expectancy)}
              </div>
              {/* 🚨 THE COVERAGE LINE IS THE WHOLE POINT OF THIS CARD'S HONESTY.
                  On this book the zero-confluence bucket is 441 trades of which
                  159 can be judged, and every tagged bucket is 100%. Without
                  this, the ladder reads as "confluences quadruple my win rate"
                  when most of the first step is trades that never had a stop. */}
              {s.scored < s.n ? (
                <div className="muted conf-tile-sub">
                  judged on {s.scored} of {s.n}
                </div>
              ) : null}
              <div className="conf-bar">
                <div
                  className={`conf-bar-fill${thin ? ' thin' : ''}`}
                  style={{ width: `${Math.max(2, Math.min(100, s.winRate ?? 0))}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <p className="muted">
        <b>Read: </b>
        {read.kind === 'climbs' ? (
          <>
            Stacking helps — your win rate climbs as you layer confluences
            ({read.steps.map((v) => (v == null ? '—' : pct(v, 0))).join(' → ')}). Watch the
            3+ bucket: a high reading on a small sample is directional, not load-bearing.
          </>
        ) : read.kind === 'plateaus' ? (
          <>
            Layering plateaus — a third confluence does not lift your win rate beyond two
            ({read.steps.slice(1).map((v) => (v == null ? '—' : pct(v, 0))).join(' → ')}). You
            may be filtering away clean trades for confirmation you do not need.
          </>
        ) : read.kind === 'mixed' ? (
          <>Mixed — no clean lift across confluence counts yet. Tag more trades to sharpen it.</>
        ) : (
          /* ⚠ THE BRANCH BETA DOES NOT HAVE. Its three cases are exhaustive, so it
             always produces a verdict — including where a bucket has nothing this
             win definition could judge. */
          <>
            Not readable yet under this win definition — at least one bucket has no trade it
            can judge{def === 'target' ? ', because almost nothing here has been reviewed against its target' : ''}.
          </>
        )}
      </p>
    </>
  );
}

function ConfluenceGrids({
  pair, pattern, preview,
}: { pair: ConfluenceGrid | null; pattern: ConfluenceGrid | null; preview: boolean }) {
  const usable = (g: ConfluenceGrid | null) => g != null && g.rows.length >= 2;
  if (!usable(pair) && !usable(pattern)) {
    return (
      <>
        <h4 className="conf-subhead">Pairwise grids</h4>
        <p className="muted">
          Neither grid has enough co-tagged trades to say anything yet. They need the same
          two confluences — or a setup and a confluence — on {CONFLUENCE_CELL_MIN}+ trades
          that also recorded an R. Switch to <b>Preview Data</b> to see the shape.
        </p>
      </>
    );
  }
  return (
    <>
      {usable(pair) ? (
        <>
          <h4 className="conf-subhead">Confluence stacking — co-occurring confluences</h4>
          <p className="muted">
            The same set on both axes. Each cell is the median R of the trades tagged with
            <b> both</b> — blue reinforces, warm fights.
            {!preview && pair && pair.populated === 0
              ? ' Every cell here is below the sample floor, which is the finding: you have not yet traded any two of these together often enough to compare them.'
              : ''}
          </p>
          <ConfluenceHeatmap grid={pair as ConfluenceGrid} />
        </>
      ) : null}
      {usable(pattern) ? (
        <>
          <h4 className="conf-subhead">Pattern × Confluence — which confluence boosts each setup</h4>
          <p className="muted">
            Rows are your setups, columns your confluences; each cell is the median R of the
            trades carrying both. <b>Redundant</b> marks a pair that is the same signal twice.
          </p>
          <ConfluenceHeatmap grid={pattern as ConfluenceGrid} />
        </>
      ) : null}
    </>
  );
}

function ConfluenceHeatmap({ grid }: { grid: ConfluenceGrid }) {
  const headH = headHeight(grid.cols);
  return (
    <div
      className="pairwise-wrap"
      style={{ ['--pw-pad-r' as string]: `${headPadRight(grid.cols)}px` }}
    >
      <table className="pairwise" style={{ ['--pw-head-h' as string]: `${headH}px` }}>
        <thead>
          <tr>
            <th className="pw-corner" />
            {grid.cols.map((c) => (
              <th key={c} className="pw-col" scope="col"><span>{c}</span></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((r) => (
            <tr key={r.label}>
              <th className="pw-row" scope="row">{r.label}</th>
              {r.cells.map((cell, i) => {
                const key = `${r.label}|${grid.cols[i] ?? i}`;
                if (cell.kind === 'diagonal') return <td key={key} className="pw-empty">—</td>;
                if (cell.kind === 'redundant') return <td key={key} className="pw-empty">redundant</td>;
                if (cell.kind === 'insufficient') {
                  return <td key={key} className="pw-empty">{cell.n === 0 ? '—' : 'insufficient'}</td>;
                }
                return (
                  <td
                    key={key}
                    className={BAND_CLASS[confluenceHeatBand(cell.medianR)] ?? 'h-neu'}
                    title={`${r.label} × ${grid.cols[i]}: ${cell.n} trades, ${cell.rSample} with a recorded R`}
                  >
                    {rr(cell.medianR)}
                    {/* ⚠ THE SAMPLE PRINTED IS THE ONE THE MEDIAN RESTS ON. Beta
                        gates on the measurable R values and then prints the trade
                        count — PD-24, on a different card. */}
                    <span className="pw-n">
                      n={cell.rSample}{cell.rSample < PAIRING_NOISY_BELOW ? ' ⚠' : ''}
                      {cell.rSample < cell.n ? ` of ${cell.n}` : ''}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
